'use client'

import { type IconDefinition } from '@fortawesome/fontawesome-common-types'
import {
  faBookOpen,
  faCircleUser,
  faComment,
  faCookieBite,
  faHeart,
  faEnvelope,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NavigationMenu } from 'radix-ui'
import { cva } from 'class-variance-authority'
import { useSession } from 'next-auth/react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { type ButtonHTMLAttributes, type ComponentProps } from 'react'
import { usePathname } from 'next/navigation'
import { cn } from 'utils/cn'
import { usePrivacyManager } from 'utils/hooks/usePrivacyManager'
import { ContactMenu } from './ContactMenu'
import { isSamePath } from './utils'

export const MobileQuickAccessMenu = () => {
  const t = useTranslations('Navigation.QuickAccessMenu')
  const openPrivacyManager = usePrivacyManager()
  const { data: session } = useSession()
  return (
    <div
      className='font-manrope text-body-md text-text-800 p-4'
      data-testid='mobile-quick-access-menu'
    >
      <LinkMenuItem
        icon={faCircleUser}
        path={session ? t('items.3.path') : t('items.4.path')}
        title={session ? t('items.3.title') : t('items.4.title')}
      />
      <LinkMenuItem
        icon={faBookOpen}
        path={t('items.1.path')}
        title={t('items.1.title')}
      />
      <LinkMenuItem
        icon={faHeart}
        path={t('items.2.path')}
        title={t('items.2.title')}
      />
      <NavigationMenu.Item>
        <NavigationMenu.Trigger className={cn(menuItem({}))}>
          <FontAwesomeIcon icon={faComment} size='lg' width={20} />
          {t('items.0.title')}
        </NavigationMenu.Trigger>
        <NavigationMenu.Content
          className={cn(
            'data-[state=open]:animate-enterFromRight bg-surface-primary data-[motion=from-end]:animate-enterFromRight data-[motion=to-start]:animate-exitToRight data-[motion=to-end]:animate-exitToRight absolute left-0 top-0 z-50 flex min-h-full w-full flex-col data-[state=closed]:-z-10 data-[state=closed]:opacity-0 data-[state=open]:opacity-100',
          )}
          forceMount
        >
          <ContactMenu />
        </NavigationMenu.Content>
      </NavigationMenu.Item>
      <LinkMenuItem
        icon={faEnvelope}
        path={t('items.5.path')}
        title={t('items.5.title')}
      />
      <ButtonMenuItem icon={faCookieBite} onClick={openPrivacyManager}>
        {t('items.6.title')}
      </ButtonMenuItem>
    </div>
  )
}

const menuItem = cva(
  `text-text-800 hover:text-primary-700 focus:text-primary-700 active:text-primary-800 flex h-12 items-center justify-start gap-2 whitespace-nowrap px-3`,
  {
    variants: {
      active: {
        true: 'text-primary-700 font-bold',
      },
    },
  },
)

const LinkMenuItem = ({
  icon,
  path,
  title,
  className,
  ...props
}: ComponentProps<typeof NavigationMenu.Link> & {
  icon: IconDefinition
  path: string
  title: string
}) => {
  return (
    <NavigationMenu.Link
      asChild
      className={cn(
        menuItem({
          active: isSamePath(usePathname(), path),
          className,
        }),
      )}
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      {...props}
    >
      <Link href={path}>
        <FontAwesomeIcon icon={icon} size='lg' width={20} />
        {title}
      </Link>
    </NavigationMenu.Link>
  )
}

const ButtonMenuItem = ({
  icon,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: IconDefinition
}) => (
  <NavigationMenu.Item>
    <NavigationMenu.Link
      asChild
      className={cn(menuItem())}
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <button {...props} type='button'>
        <FontAwesomeIcon icon={icon} size='lg' width={20} />
        {children}
      </button>
    </NavigationMenu.Link>
  </NavigationMenu.Item>
)
